import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import car from 'src/assets/images/car.png';
import { PATH } from 'src/features/navigation/utils/constants';
import { useListActiveStays } from 'src/features/stay/api/useListActiveStays';
import { useListPastStays } from 'src/features/stay/api/useListPastStays';
import { useGetVisitor } from 'src/features/visitor/api/useGetVisitor';
import GatewayPassesTable from 'src/features/gatewayPass/components/GatewayPassesTable';
import PastStaysTable from 'src/features/stay/components/PastStaysTable';
import { ActiveStay, PastStay, STAY_TYPE } from 'src/features/stay/types';
import { Visitor } from 'src/features/visitor/types';
import { Translation } from 'src/types';
import { visitorTypeBadgeOptions } from 'src/features/visitor/utils/constants';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { useGetStayDuration } from 'src/hooks/useGetStayDuration';
import {
    GeneralBlock,
    GeneralBlockBody,
    GeneralBlockHeader,
    GeneralBlockTitle,
} from 'src/registry/components/blocks/GeneralBlock';
import { Badge } from 'src/registry/ui/Badge';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'src/registry/ui/Tabs';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';
import { useParking } from 'src/providers/ParkingProvider';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import PaymentsTable from 'src/features/payment/components/tables/PaymentsTable';
import BackButton from 'src/features/navigation/components/BackButton';

export default function VisitorsDashboard() {
    const { visitorId = '' } = useParams();
    const { formatMessage, locale } = useIntl();

    const { getDurationFromStartDateToEndDate } = useGetStayDuration();
    const { setTitle, removeTitle } = useTitleStore();
    const { parkingId } = useParking();

    const { data, isLoading } = useGetVisitor({ parkingId, visitorId });

    const search = React.useMemo(() => (data ? data.main_identifier?.value : ''), [data]);
    const { data: activeStaysData, isLoading: activeStaysLoading } = useListActiveStays({
        parkingId,
        search,
        limit: 1,
        config: { enabled: !!search },
    });

    const { data: pastStaysData } = useListPastStays({
        parkingId,
        search,
        limit: 1,
        config: { enabled: !!search && !activeStaysData?.count && !isLoading && !activeStaysLoading },
        ordering: '-entry_time',
    });

    React.useEffect(() => {
        if (data && data.main_identifier?.value) setTitle(data.main_identifier.value);

        return removeTitle;
    }, [data, removeTitle, setTitle]);

    const [stay, stayType]: [ActiveStay | PastStay | undefined | null, STAY_TYPE | null] = React.useMemo(() => {
        if (activeStaysData?.count !== 0) {
            return [activeStaysData?.results[0], STAY_TYPE.ACTIVE];
        }
        if (pastStaysData?.count !== 0) {
            return [pastStaysData?.results[0], STAY_TYPE.PAST];
        }
        return [null, null];
    }, [activeStaysData, pastStaysData]);

    const visitorType = React.useMemo(() => (data ? visitorTypeBadgeOptions[data.type] : null), [data]);

    const translate = React.useCallback(
        (translations?: Translation[]) => {
            if (!translations) return '-';
            const localeTranslation = translations.find((translation) => translation.language === locale)?.name;
            return localeTranslation ?? '-';
        },
        [locale],
    );

    const translateZones = React.useCallback(
        (values?: Visitor['zones']) => {
            if (!values) return '-';
            const filteredTranslations = values.map(({ translations }) => translate(translations));
            return filteredTranslations.join(', ') || '-';
        },
        [translate],
    );

    const permissionActiveStay = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const permissionPastStay = useHasPermissions([PERMISSION.ACCESS_PAST_STAYS]);
    const permissionGatewayPasses = useHasPermissions([PERMISSION.ACCESS_GATEWAY_PASSES]);
    const permissionPayments = useHasPermissions([PERMISSION.ACCESS_PAYMENTS]);
    const permissionAccessEntity = useHasPermissions([PERMISSION.ACCESS_ENTITIES]);

    const accessStayPermission = stayType === STAY_TYPE.ACTIVE ? permissionActiveStay : permissionPastStay;

    const defaultTabValue = (() => {
        if (accessStayPermission) return 'visits';
        if (permissionGatewayPasses) return 'gateway-passes';
        if (permissionPayments) return 'payments';
    })();

    const parkingStayRerouteUrl = React.useMemo(() => {
        if (!stay?.id || !stayType) return undefined;

        return `/${PATH.VISITORS}/${stayType}-stay/dashboard/${stay?.id}`;
    }, [stay, stayType]);

    return (
        <>
            <BackButton customRoute={`/${PATH.VISITORS}/visitors`} />
            <div className="flex flex-col gap-9">
                <div className="flex gap-9">
                    <GeneralBlock className="flex-1 items-center justify-center">
                        <img src={car} alt="Car" />
                    </GeneralBlock>
                    <GeneralBlock className="grid flex-3 grid-cols-3">
                        <div className="flex flex-col justify-between">
                            {isLoading ? (
                                <Skeleton className="size-14 rounded-full" />
                            ) : (
                                <div>
                                    <div
                                        className={cn(
                                            'flex size-14 items-center justify-center rounded-full border-8 p-1',
                                            data?.in_parking
                                                ? 'border-green-50 bg-green-100 text-green-700'
                                                : 'border-red-50 bg-red-100 text-red-700',
                                        )}
                                    >
                                        <FontAwesomeIcon icon={ICON_MAP.WAVE_PULSE} className="text-2xl" />
                                    </div>
                                </div>
                            )}
                            <div className="flex flex-col">
                                <GeneralBlockHeader variant="dashboard">
                                    <GeneralBlockTitle className="text-base text-gray-900">
                                        <FormattedMessage {...visitorMessages.currentStatus} />
                                    </GeneralBlockTitle>
                                </GeneralBlockHeader>
                                <GeneralBlockBody variant="dashboard">
                                    {isLoading ? (
                                        <Skeleton className="h-6 w-full max-w-48" />
                                    ) : (
                                        <p className="text-xl font-semibold lg:text-3xl">
                                            <FormattedMessage
                                                {...(data?.in_parking
                                                    ? visitorMessages.inParking
                                                    : visitorMessages.notInParking)}
                                            />
                                        </p>
                                    )}
                                </GeneralBlockBody>
                            </div>
                        </div>
                        <div className="flex flex-col gap-4">
                            <GeneralBlockAttribute
                                label={formatMessage(
                                    data?.in_parking ? visitorMessages.activeVisit : visitorMessages.lastVisit,
                                )}
                                value={stay?.ref}
                                link={accessStayPermission ? parkingStayRerouteUrl : undefined}
                                icon={ICON_MAP.ARROW_UP_RIGHT}
                            />
                            <GeneralBlockAttribute
                                label={formatMessage(visitorMessages.parkingZone)}
                                value={translate(stay?.period_zone?.translations)}
                            />
                            <GeneralBlockAttribute
                                label={formatMessage(visitorMessages.identifiers)}
                                value={stay?.main_identifier?.value}
                            />
                        </div>
                        <div className="flex flex-col gap-4">
                            <GeneralBlockAttribute
                                label={formatMessage(visitorMessages.enteredAt)}
                                value={getFormattedDateTimeForDetailPanel(stay?.entry_time)}
                            />
                            <GeneralBlockAttribute
                                label={formatMessage(visitorMessages.exitedAt)}
                                value={getFormattedDateTimeForDetailPanel(stay?.exit_time)}
                            />
                            <GeneralBlockAttribute
                                label={formatMessage(visitorMessages.duration)}
                                value={getDurationFromStartDateToEndDate({
                                    startDate: stay?.entry_time,
                                    endDate: stay?.exit_time,
                                })}
                            />
                        </div>
                    </GeneralBlock>
                </div>
                <div className="flex gap-9">
                    <GeneralBlock className="flex max-h-max flex-1 flex-col">
                        <GeneralBlockAttribute
                            label={formatMessage(visitorMessages.owner)}
                            value={data?.owner?.name}
                            link={
                                permissionAccessEntity && data?.owner
                                    ? `/${PATH.ENTITIES}/${data?.owner.type}/${data?.owner?.id}`
                                    : undefined
                            }
                            icon={ICON_MAP.ARROW_UP_RIGHT}
                        />
                        <GeneralBlockAttribute
                            label={formatMessage(visitorMessages.identifiers)}
                            value={data?.main_identifier?.value}
                        />
                        <div className="flex flex-col">
                            <GeneralBlockHeader variant="dashboard">
                                <GeneralBlockTitle>{formatMessage(visitorMessages.type)}</GeneralBlockTitle>
                            </GeneralBlockHeader>
                            <GeneralBlockBody variant="dashboard">
                                {isLoading || !visitorType ? (
                                    <Skeleton className="h-6 w-full max-w-48" />
                                ) : (
                                    <Badge theme={visitorType.theme}>
                                        {visitorType.icon && <FontAwesomeIcon icon={visitorType.icon} />}
                                        {formatMessage(visitorType.label)}
                                    </Badge>
                                )}
                            </GeneralBlockBody>
                        </div>
                        <GeneralBlockAttribute
                            label={formatMessage(visitorMessages.visitedZones)}
                            value={translateZones(data?.zones)}
                        />
                        {data &&
                            Object.entries(data.attributes).map(([key, value]) => (
                                <GeneralBlockAttribute label={key} value={value} key={key} />
                            ))}
                    </GeneralBlock>

                    <div className="grid flex-3 grid-cols-1">
                        <Tabs defaultValue={defaultTabValue}>
                            <TabsList>
                                {permissionPastStay && (
                                    <TabsTrigger value="visits">
                                        <FormattedMessage {...visitorMessages.pastStays} />
                                    </TabsTrigger>
                                )}
                                {permissionGatewayPasses && (
                                    <TabsTrigger value="gateway-passes">
                                        <FormattedMessage {...visitorMessages.gatewayPasses} />
                                    </TabsTrigger>
                                )}
                                {permissionPayments && (
                                    <TabsTrigger value="payments">
                                        <FormattedMessage {...visitorMessages.payments} />
                                    </TabsTrigger>
                                )}
                            </TabsList>

                            {permissionPastStay && (
                                <TabsContent value="visits" className="w-full">
                                    <PastStaysTable visitor={visitorId} />
                                </TabsContent>
                            )}
                            {permissionGatewayPasses && (
                                <TabsContent value="gateway-passes">
                                    <GatewayPassesTable visitor={visitorId} />
                                </TabsContent>
                            )}
                            {permissionPayments && (
                                <TabsContent value="payments">
                                    <PaymentsTable visitorId={visitorId} />
                                </TabsContent>
                            )}
                        </Tabs>
                    </div>
                </div>
            </div>
        </>
    );
}
